import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Plus, X } from "lucide-react";
import clsx from "clsx";
import { Badge, Banner, Button, Field, FilterChips, Input, PageHeader, SearchInput, Segmented, Select, Skeleton, Spinner, Textarea, EmptyState, apiFetch, hasPermission, useApi, useCurrentUser, useT, useToast } from "@iga/ui";
import type { CatalogApplication, CatalogItem, Person, RequestDetail, RequestItemInput, RequestPreview, TeamMember } from "../lib/types";
import { REQUEST_KEYS, errMsg, firstName } from "../lib/hooks";
import { Avatar, LoadError, NoIdentity, PeoplePicker, PersonLine, PortalRisk, SidePanel, SodNotes } from "../components/common";

type Mode = "me" | "team" | "any";
interface CartLine { item: CatalogItem; validTo: string }
const keyOf = (i: { kind: string; id: string }) => `${i.kind}:${i.id}`;

/** One catalogue result (§6 J1): name, application, description, risk from High up, and what the catalogue knows about the person. */
function ResultRow({ it, forName, self, inCart, onRequest, onRemove }: { it: CatalogItem; forName: string; self: boolean; inCart: boolean; onRequest: () => void; onRemove: () => void }) {
  const t = useT();
  const where = it.application?.displayName ?? it.system?.displayName ?? "";
  return (
    <li className="flex flex-col gap-2 px-4 py-3.5 sm:flex-row sm:items-start sm:gap-4">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <h3 className="text-h3">{it.displayName}</h3>
          <span className="text-small text-fg-3">{where}</span>
          <PortalRisk risk={it.risk} />
        </div>
        {it.description && <p className="mt-0.5 text-fg-2">{it.description}</p>}
        {it.held && <p className="mt-1.5 text-small text-fg-2"><Badge tone="neutral">{self ? t("p.catalog.youHave") : t("p.catalog.has", { name: firstName(forName) })}</Badge></p>}
        {it.pending && <p className="mt-1.5 flex flex-wrap items-center gap-2 text-small"><Badge tone="info">{t("p.catalog.alreadyRequested")}</Badge>{it.pendingRequestId && <Link to={`/requests/${it.pendingRequestId}`}>{t("p.catalog.seeRequest")}</Link>}</p>}
        {it.sod.length > 0 && (
          <div className="mt-1.5 flex flex-col gap-1">
            <SodNotes sod={it.sod} holder={self ? t("p.you") : forName} />
            <p className="text-small text-fg-3">{it.blocked ? t("p.catalog.blockedHint", { name: self ? t("p.you") : firstName(forName) }) : t("p.catalog.detectHint")}</p>
          </div>
        )}
      </div>
      <div className="flex-none">
        {it.held || it.pending ? null
          : inCart ? <Button variant="secondary" size="touch" icon={<Check className="h-4 w-4" aria-hidden="true" />} onClick={onRemove} aria-label={t("p.catalog.removeFrom", { item: it.displayName })}>{t("p.catalog.inRequest")}</Button>
          : <Button variant="secondary" size="touch" disabled={it.blocked} onClick={onRequest} aria-label={t("p.catalog.requestItem", { item: it.displayName })}>{t("p.catalog.request")}</Button>}
      </div>
    </li>
  );
}

export function RequestAccessPage() {
  const t = useT();
  const toast = useToast();
  const nav = useNavigate();
  const qc = useQueryClient();
  const me = useCurrentUser();
  const [params] = useSearchParams();
  const canAny = hasPermission(me?.user.permissions, "requests:write");
  const canSearchIdentities = hasPermission(me?.user.permissions, "identities:read");
  const self: Person | null = me?.identity ? { id: me.identity.id, displayName: me.identity.displayName, email: me.identity.email ?? null } : null;
  const forParam = params.get("for");

  const [mode, setMode] = useState<Mode>(forParam === "team" ? "team" : forParam && forParam !== "me" ? (me?.isManager ? "team" : "any") : self ? "me" : me?.isManager ? "team" : "any");
  const [beneficiary, setBeneficiary] = useState<Person | null>(forParam && forParam !== "me" ? null : self);
  const [choosing, setChoosing] = useState(false);
  const [q, setQ] = useState(""); const [dq, setDq] = useState("");
  const [appId, setAppId] = useState("");
  const [allApps, setAllApps] = useState(false);
  const [cart, setCart] = useState<Map<string, CartLine>>(new Map());
  const [panel, setPanel] = useState(false);
  const [justification, setJustification] = useState("");
  const [jError, setJError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  useEffect(() => { const h = setTimeout(() => setDq(q.trim()), 250); return () => clearTimeout(h); }, [q]);

  const team = useApi<{ items: TeamMember[] }>(["team"], "/api/portal/team", undefined, { enabled: !!me?.isManager });
  const apps = useApi<{ items: CatalogApplication[] }>(["catalog-apps"], "/api/portal/catalog/applications");
  const catalog = useApi<{ for: string; items: CatalogItem[] }>(["catalog", beneficiary?.id], "/api/portal/catalog", { for: beneficiary?.id, q: dq, applicationId: appId }, { enabled: !!beneficiary });

  // Deep link ?for=<identityId> (team page, service desk).
  useEffect(() => {
    if (!forParam || forParam === "me" || forParam === "team") return;
    const member = team.data?.items.find((x) => x.id === forParam);
    if (member) { setBeneficiary({ id: member.id, displayName: member.displayName, email: member.email }); setMode("team"); return; }
    if (canSearchIdentities && !team.isLoading) apiFetch<{ identity: Person }>(`/api/portal/servicedesk/identities/${forParam}`).then((d) => { setBeneficiary({ id: d.identity.id, displayName: d.identity.displayName, email: d.identity.email }); setMode("any"); }).catch(() => undefined);
  }, [forParam, team.data, team.isLoading, canSearchIdentities]);

  const lines = useMemo(() => [...cart.values()], [cart]);
  const isSelf = !!beneficiary && beneficiary.id === self?.id;
  const items: RequestItemInput[] = useMemo(() => lines.map((l) => ({ kind: l.item.kind, targetId: l.item.id, action: "grant", validTo: l.validTo ? new Date(`${l.validTo}T23:59:59`).toISOString() : null })), [lines]);
  const preview = useQuery<RequestPreview, Error>({
    queryKey: ["request-preview", beneficiary?.id, items],
    queryFn: () => apiFetch<RequestPreview>("/api/portal/requests/preview", { method: "POST", json: { beneficiaryId: beneficiary!.id, items } }),
    enabled: !!beneficiary && items.length > 0 && panel && !!me?.identity, staleTime: 30_000, retry: false,
  });

  function changeMode(m: Mode) { setMode(m); setCart(new Map()); setPanel(false); if (m === "me") { setBeneficiary(self); setChoosing(false); } else { setBeneficiary(null); setChoosing(true); } }
  function add(item: CatalogItem) { setCart((c) => { const n = new Map(c); n.set(keyOf(item), { item, validTo: "" }); return n; }); setPanel(true); }
  function remove(k: string) { setCart((c) => { const n = new Map(c); n.delete(k); if (n.size === 0) setPanel(false); return n; }); }
  function setUntil(k: string, v: string) { setCart((c) => { const n = new Map(c); const l = n.get(k); if (l) n.set(k, { ...l, validTo: v }); return n; }); }

  async function submit() {
    if (!beneficiary) return;
    if (!justification.trim()) { setJError(t("p.request.justificationRequired")); document.getElementById("justification")?.focus(); return; }
    setSubmitting(true); setSubmitError(null);
    try {
      const path = me?.identity ? "/api/portal/requests" : "/api/portal/servicedesk/requests";
      const r = await apiFetch<RequestDetail>(path, { method: "POST", json: { beneficiaryId: beneficiary.id, items, justification: justification.trim() } });
      for (const k of REQUEST_KEYS) void qc.invalidateQueries({ queryKey: k });
      const first = r.steps.find((s) => s.state === "pending");
      toast.push(first?.approverName ? t("p.request.sentTo", { name: first.approverName }) : t("p.request.sent"), { tone: "ok" });
      nav(`/requests/${r.id}`);
    } catch (e) { setSubmitError(e); toast.push(errMsg(e), { tone: "bad" }); }
    finally { setSubmitting(false); }
  }

  const modeOptions = [
    ...(self ? [{ value: "me" as Mode, label: t("p.request.forMe") }] : []),
    ...(me?.isManager ? [{ value: "team" as Mode, label: t("p.request.forTeam") }] : []),
    ...(canAny || canSearchIdentities ? [{ value: "any" as Mode, label: t("p.request.forAnyone") }] : []),
  ];
  const appItems = apps.data?.items ?? [];
  const visibleApps = allApps ? appItems : appItems.slice(0, 5);
  const results = catalog.data?.items ?? [];
  const forName = beneficiary?.displayName ?? "";
  const previewFor = (key: string) => preview.data?.items.find((p) => `${p.kind}:${p.targetId}` === key);
  const slaDays = Math.max(...(preview.data?.items.map((p) => p.slaHours ?? 0) ?? [0])) / 24;

  if (!self && !canAny && !canSearchIdentities) return <><PageHeader portal title={t("p.nav.request")} /><NoIdentity what={t("p.request.noIdentity")} /></>;

  return (
    <div className="flex flex-col gap-5">
      <PageHeader portal title={t("p.nav.request")} subtitle={t("p.request.subtitle")} />

      {/* For */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-semibold">{t("p.request.for")}</span>
          {modeOptions.length > 1 && <Segmented<Mode> ariaLabel={t("p.request.for")} variant="pill" size="touch" value={mode} onChange={changeMode} options={modeOptions} />}
          {beneficiary && mode !== "me" && !choosing && (
            <span className="inline-flex items-center gap-2 rounded-full border border-line bg-surface py-1 pl-1 pr-2">
              <Avatar name={beneficiary.displayName} size={28} /><span className="font-semibold">{beneficiary.displayName}</span>
              <button type="button" className="min-h-9 px-1 font-semibold text-brand-text hover:underline" onClick={() => setChoosing(true)}>{t("p.request.change")}</button>
            </span>
          )}
        </div>
        {mode === "team" && (choosing || !beneficiary) && (
          <div className="rounded-card border border-line bg-surface p-3">
            {team.isLoading ? <Skeleton rows={3} /> : team.error ? <LoadError error={team.error} /> : (
              <ul className="grid grid-cols-1 gap-1 sm:grid-cols-2 lg:grid-cols-3" aria-label={t("p.request.pickPerson")}>
                {team.data?.items.filter((x) => x.status !== "inactive").map((x) => (
                  <li key={x.id}>
                    <button type="button" className={clsx("flex min-h-12 w-full items-center gap-2.5 rounded-control px-2 text-left hover:bg-surface-2", beneficiary?.id === x.id && "bg-brand-soft")} onClick={() => { setBeneficiary({ id: x.id, displayName: x.displayName, email: x.email }); setChoosing(false); setCart(new Map()); setPanel(false); }}>
                      <PersonLine person={x} secondary={x.jobTitle ?? x.email} size={32} />
                    </button>
                  </li>
                ))}
                {team.data?.items.length === 0 && <li className="p-2 text-fg-3">{t("p.team.none")}</li>}
              </ul>
            )}
          </div>
        )}
        {mode === "any" && (choosing || !beneficiary) && (
          <div className="max-w-lg">
            <Field label={t("p.request.pickPerson")} htmlFor="beneficiary">
              <PeoplePicker id="beneficiary" value={beneficiary} onChange={(p) => { setBeneficiary(p); if (p) setChoosing(false); setCart(new Map()); setPanel(false); }} autoFocus
                fetchPath={canSearchIdentities ? "/api/portal/servicedesk/search" : "/api/portal/me/people"} />
            </Field>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-[minmax(0,1fr)_400px]">
        <div className="flex min-w-0 flex-col gap-4">
          <div className="flex flex-col gap-3">
            <SearchInput value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("p.request.searchPlaceholder")} aria-label={t("p.request.searchPlaceholder")} autoFocus={!!beneficiary} disabled={!beneficiary} className="max-w-2xl" />
            {appItems.length > 0 && (
              <div className="flex flex-wrap items-center gap-2">
                <FilterChips ariaLabel={t("p.request.applications")} value={appId} onChange={setAppId} items={[{ id: "", label: t("p.request.allApplications") }, ...visibleApps.map((a) => ({ id: a.id, label: a.displayName }))]} />
                {appItems.length > 5 && <button type="button" className="min-h-8 text-small font-semibold text-brand-text hover:underline" onClick={() => setAllApps((v) => !v)}>{allApps ? t("p.request.fewerApplications") : t("p.request.browseAll", { count: appItems.length })}</button>}
              </div>
            )}
          </div>
          {!beneficiary ? <p className="text-fg-3">{t("p.request.chooseFirst")}</p>
            : catalog.isLoading ? <div className="rounded-card border border-line bg-surface p-4"><Skeleton rows={4} /></div>
            : catalog.error ? <LoadError error={catalog.error} onRetry={() => void catalog.refetch()} />
            : results.length === 0 ? <EmptyState kind="no-results" title={t("p.request.noResults")} body={dq || appId ? t("p.request.noResultsHint") : t("p.request.nothingPublished")} />
            : (
              <div className="rounded-card border border-line bg-surface overflow-hidden">
                <div className="border-b border-line px-4 py-2 text-small text-fg-3">{t("p.request.results", { count: results.length })}</div>
                <ul className="divide-y divide-line">
                  {results.map((it) => <ResultRow key={keyOf(it)} it={it} forName={forName} self={isSelf} inCart={cart.has(keyOf(it))} onRequest={() => add(it)} onRemove={() => remove(keyOf(it))} />)}
                </ul>
              </div>
            )}
          {lines.length > 0 && !panel && (
            <div className="sticky bottom-20 z-20 flex items-center justify-between gap-3 rounded-card border border-line bg-surface p-3 shadow-e1 md:bottom-4">
              <span className="font-semibold">{t("p.request.inRequest", { count: lines.length })}</span>
              <Button size="touch" onClick={() => setPanel(true)}>{t("p.request.review")}</Button>
            </div>
          )}
        </div>

        <div className={clsx(!panel && "hidden lg:block")}>
          {panel && beneficiary && lines.length > 0 ? (
            <SidePanel open onClose={() => setPanel(false)} title={t("p.request.panelTitle", { name: isSelf ? t("p.request.forMeTitle") : beneficiary.displayName })}
              footer={<>
                <Button size="lg" loading={submitting} onClick={() => void submit()} disabled={preview.data?.items.every((p) => p.blocked || p.error)}>{t("p.request.send")}</Button>
                <Button size="touch" variant="ghost" onClick={() => { setPanel(false); document.querySelector<HTMLInputElement>("input[type=search]")?.focus(); }} icon={<Plus className="h-4 w-4" aria-hidden="true" />}>{t("p.request.addAnother")}</Button>
              </>}>
              <div className="flex flex-col gap-5">
                <ul className="flex flex-col gap-3">
                  {lines.map((l) => { const k = keyOf(l.item); const p = previewFor(k); return (
                    <li key={k} className="flex flex-col gap-2 rounded-control border border-line p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0"><div className="font-semibold">{l.item.displayName}</div><div className="text-small text-fg-3">{l.item.application?.displayName ?? l.item.system?.displayName}<PortalRisk risk={l.item.risk} className="ml-2" /></div></div>
                        {lines.length > 1 && <Button variant="ghost" size="touch" className="-mr-2 px-2" onClick={() => remove(k)} aria-label={t("p.catalog.removeFrom", { item: l.item.displayName })}><X className="h-4 w-4" aria-hidden="true" /></Button>}
                      </div>
                      <SodNotes sod={l.item.sod} holder={isSelf ? t("p.you") : forName} />
                      {p?.error && <Banner tone="bad" title={p.error} />}
                      <Field label={t("p.request.until")} htmlFor={`until-${k}`}>
                        <div className="flex flex-col gap-2 sm:flex-row">
                          <Select id={`until-${k}`} value={l.validTo ? "date" : ""} onChange={(e) => setUntil(k, e.target.value === "date" ? new Date(Date.now() + 90 * 86_400_000).toISOString().slice(0, 10) : "")}>
                            <option value="">{l.item.defaultDurationDays ? t("p.request.roleDefault", { days: l.item.defaultDurationDays }) : t("p.approval.noEndDate")}</option>
                            <option value="date">{t("p.request.untilDate")}</option>
                          </Select>
                          {l.validTo && <Input type="date" value={l.validTo} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setUntil(k, e.target.value)} aria-label={t("p.request.untilDate")} />}
                        </div>
                      </Field>
                    </li>
                  ); })}
                </ul>
                <Field label={isSelf ? t("p.request.whyMe") : t("p.request.why", { name: firstName(beneficiary.displayName) })} hint={t("p.request.whyHint")} error={jError} htmlFor="justification">
                  <Textarea id="justification" value={justification} onChange={(e) => { setJustification(e.target.value); if (jError) setJError(null); }} invalid={!!jError} rows={4} maxLength={4000} />
                </Field>
                <div className="flex flex-col gap-2">
                  <h3 className="text-h3">{t("p.request.whoDecides")}</h3>
                  {!me?.identity ? <p className="text-small text-fg-3">{t("p.request.previewUnavailable")}</p>
                    : preview.isLoading ? <span className="inline-flex items-center gap-2 text-small text-fg-3"><Spinner className="h-4 w-4" />{t("ui.loading")}</span>
                    : preview.error ? <p className="text-small text-bad">{errMsg(preview.error)}</p>
                    : preview.data && (
                      <div className="flex flex-col gap-3">
                        {preview.data.items.map((p) => (
                          <div key={`${p.kind}:${p.targetId}`} className="flex flex-col gap-1.5">
                            {lines.length > 1 && <div className="text-small font-semibold text-fg-2">{p.displayName}</div>}
                            {p.blocked ? <p className="text-small text-fg-2">{t("p.request.wouldBeBlocked")}</p>
                              : p.autoApprove ? <p className="text-fg-2">{t("p.request.autoApprove")}</p>
                              : (
                                <ol className="flex flex-col gap-1">
                                  {p.steps.map((s) => (
                                    <li key={s.level} className="flex flex-wrap items-baseline gap-x-2">
                                      <span className="w-5 flex-none text-small text-fg-3">{s.level}</span>
                                      <span className={clsx("font-semibold", s.skipped && "font-normal text-fg-3 line-through")}>{s.approver?.id === me?.identity?.id ? t("p.you") : s.approver?.displayName ?? (s.group ? t("p.approval.group", { group: s.group }) : t("p.approval.unassigned"))}</span>
                                      <span className="text-small text-fg-3">{t(`p.approverKind.${s.approverKind}`)}{s.skipped ? ` · ${t("p.request.skippedSelf")}` : ""}</span>
                                    </li>
                                  ))}
                                </ol>
                              )}
                          </div>
                        ))}
                        {slaDays > 0 && <p className="text-small text-fg-3">{t("p.request.dueWithin", { days: Math.round(slaDays) })} {isSelf ? t("p.request.emailMe") : t("p.request.emailBoth", { name: firstName(beneficiary.displayName) })}</p>}
                      </div>
                    )}
                </div>
                <LoadError error={submitError} title={t("p.request.sendFailed")} />
              </div>
            </SidePanel>
          ) : (
            <div className="hidden lg:block">
              <div className="rounded-card border border-line bg-surface p-5 text-fg-3">
                <h2 className="text-h3 text-fg">{t("p.request.panelEmptyTitle")}</h2>
                <p className="mt-1">{t("p.request.panelEmptyBody")}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
